import { getApiClient } from '../../../api';
import { Messages } from 'primereact/messages';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingPage from '../../../components/LoadingPage';
import { getErrorMessage } from '../../../utils/errors';
import { ErrorMessage } from '../../components/ErrorMessage';
import { MunicipalityForm } from '../../components/MunicipalityForm';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { scrollTop } from '../../utils/scroll';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { hasRole } from '../../utils/auth';
import { UserRole } from '../../constants/enums';

export default function MunicipalityEdit() {
    const { municipalityId } = useParams();

    const { user } = useUserPermissions();

    const msgs = useRef(null);
    const refForm = useRef(null);

    const [loading, setLoading] = useState(true);
    const [initError, setInitError] = useState(null);
    const [bodyTypeOptions, setBodyTypeOptions] = useState([]);
    const [municipalityTypeOptions, setMunicipalityTypeOptions] = useState([]);
    const [municipality, setMunicipality] = useState(null);

    const mapMunicipalityData = (municipalityData) => {

        const mappedData = {
            text_id: municipalityData.text_id,
            municipality_type: municipalityData.type_id,
            name: municipalityData.name,
            name_genitive: municipalityData.name_genitive,
            logo: municipalityData.logo,
            color: municipalityData.color,
            url: municipalityData.url,
            email: municipalityData.email,
            partner: municipalityData.partner,
            zastupitelstvo_application_name: '',
            zastupitelstvo_name: '',
            zastupitelstvo_name_genitive: '',
            zastupitelstvo_place: '',
            zastupitelstvo_member_count: '',
            zastupitelstvo_application_description: ''
        };

        if (municipalityData.bodies) {
            mappedData.bodies = Object.keys(municipalityData.bodies).reduce((result, bodyKey) => {
                const body = municipalityData.bodies[bodyKey];
                result[bodyKey] = {
                    application_name: body.application_name,
                    name: body.name,
                    name_genitive: body.name_genitive,
                    place: body.place,
                    member_count: body.member_count,
                    application_description: body.application_description,
                    data_url: body.data_url
                };
                return result;
            }, {});
        }

        if (municipalityData.features) {
            mappedData.features = Object.keys(municipalityData.features).reduce((result, featureKey) => {
                const feature = municipalityData.features[featureKey];
                result[featureKey] = {
                    id: feature.id,
                    name: feature.name
                };
                return result;
            }, {});
        }

        return mappedData;
    };

    const loadMunicipality = useCallback(async () => {
        const municipalityResponse = await getApiClient().getAdminMunicipalityByMunicipalityId(municipalityId);
        setMunicipality(mapMunicipalityData(municipalityResponse));
    }, [municipalityId]);

    useEffect(() => {
        const loadMunicipalityTypes = async () => {
            const municipalityTypesResponse = await getApiClient().getAdminMunicipalityType();
            setMunicipalityTypeOptions(
                municipalityTypesResponse.map((typMunicipality) => ({
                    label: typMunicipality.code,
                    value: typMunicipality.id
                }))
            );
        };

        const loadBodyTypes = async () => {
            const bodyTypesResponse = await getApiClient().getAdminBodyType();
            setBodyTypeOptions(
                bodyTypesResponse.map((typOrganu) => ({
                    label: typOrganu.code,
                    value: typOrganu.id,
                    text_value: typOrganu.code
                }))
            );
        };

        const fetchData = async () => {
            try {
                await Promise.all([loadMunicipalityTypes(), loadBodyTypes(), loadMunicipality()]);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                setInitError(errorMessage ?? 'Nepodařilo se načíst data zvolené municipality');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [loadMunicipality]);

    const onSubmit = async (data) => {
        const requestData = {
            municipality_type: data.municipality_type,
            name: data.name,
            name_genitive: data.name_genitive,
            color: data.color,
            url: data.url,
            email: data.email ? data.email : null,
            track_changes: data.track_changes,
            zastupitelstvo_application_name: data.zastupitelstvo_application_name,
            zastupitelstvo_name: data.zastupitelstvo_name,
            zastupitelstvo_name_genitive: data.zastupitelstvo_name_genitive,
            zastupitelstvo_place: data.zastupitelstvo_place,
            zastupitelstvo_member_count: data.zastupitelstvo_member_count,
            zastupitelstvo_application_description: data.zastupitelstvo_application_description,
            zastupitelstvo_data_url: data.zastupitelstvo_data_url,
            body_type: data.body_type
        };
        if (data.partner !== undefined) {
            requestData.partner = data.partner;
        }
        try {
            await getApiClient().putAdminMunicipalityByMunicipalityId(municipalityId, requestData);
            showSuccessMessage(msgs, 'Municipalita byla úspěšně upravena', { replace: true });

            if (data.logo) {
                try {
                    await getApiClient().postAdminLogoByMunicipalityId(municipalityId, {
                        logo: data.logo
                    });
                } catch (error) {
                    const errorMessage = getErrorMessage(error);
                    const baseErrorMessage = 'Nepodařilo se uložit logo municipality';
                    showErrorMessage(msgs, errorMessage ? `${baseErrorMessage}: ${errorMessage}` : baseErrorMessage);
                }

                await loadMunicipality();
                refForm?.current?.resetLogo();
            }
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se upravit municipalitu', { replace: true });
        } finally {
            scrollTop();
        }
    };

    return loading ? (
        <LoadingPage />
    ) : initError ? (
        <>
            <h1>Upravit Municipalitu</h1>

            <ErrorMessage prefix='Nelze použít formulář' error={initError} />
        </>
    ) : (
        <>
            <h1>Upravit Municipalitu {`${municipality.name} (${municipality.text_id})`}</h1>

            <Messages ref={msgs} className='page__messages' />

            <MunicipalityForm
                ref={refForm}
                municipalityTypeOptions={municipalityTypeOptions}
                bodyTypeOptions={bodyTypeOptions}
                onSubmit={onSubmit}
                editMunicipality={municipality}
                enablePartner={hasRole(user, UserRole.ADMIN)}
            />
        </>
    );
}
